import React, { useState } from "react";
import NotificationsIcon from "@material-ui/icons/Notifications";
import { Badge, Box, Popover, useMediaQuery } from "@material-ui/core";
import {
    useBadgeStyles,
    useFullScreenPopoverStyles,
    usePopoverClasses,
    usePopoverStyles,
} from "components/notifications/notificationsButton/styles";
import IconButton from "uiKit/buttons/IconButton";
import { Theme } from "@material-ui/core/styles";
import { mobileBreakpoint } from "shared/theme/defaultTheme";
import { observer } from "mobx-react";
import { useRootStore } from "app/useRootStore";
import { useRequestContext } from "shared/api/RequestProvider";
import NotificationsContainer from "components/notifications/notificationsContainer";
import useAsyncEffect from "use-async-effect";

const NotificationsButton = (): JSX.Element => {
    const badgeStyles = useBadgeStyles();
    const popoverStyles = usePopoverStyles();
    const fullScreenPopoverClasses = useFullScreenPopoverStyles();
    const popoverClasses = usePopoverClasses();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [buttonPalette, setButtonPalette] = useState<"default" | "primary">("default");
    const isMobileScreenSize = useMediaQuery<Theme>(theme => theme.breakpoints.down(mobileBreakpoint));
    const {
        rootStore: {
            appNotificationsStore: {
                notifications,
                notReadCount,
                startFetchNotifications,
                stopFetchNotifications,
                stopFetchNotReadCount,
                startFetchNotReadCount,
                deleteAllNotifications,
                deleteNotification,
                markAllAsRead,
            },
            appSettingsStore: { appSettings },
        },
    } = useRootStore();

    const { executeRequest, isLoading } = useRequestContext();

    useAsyncEffect(async () => {
        if (appSettings?.appNotificationsSettings?.isEnabled) {
            await startFetchNotReadCount(appSettings.appNotificationsSettings.pullIntervalMs);
        }
    }, [appSettings, startFetchNotReadCount]);

    const openPopover = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        if (appSettings?.appNotificationsSettings.isEnabled) {
            executeRequest(startFetchNotifications, "", [appSettings.appNotificationsSettings.pullIntervalMs]);
        }
        stopFetchNotReadCount();
    };

    const closePopover = async () => {
        setButtonPalette("default");
        setAnchorEl(null);
        if (!appSettings) {
            return;
        }
        await markAllAsRead();
        if (appSettings.appNotificationsSettings.isEnabled) {
            stopFetchNotifications();
            await startFetchNotReadCount(appSettings.appNotificationsSettings.pullIntervalMs);
        }
    };

    const open = Boolean(anchorEl);

    return (
        <>
            <Box onClick={openPopover}>
                <Badge id="notification-badge" classes={{ ...badgeStyles }} badgeContent={notReadCount} color="error">
                    <IconButton
                        size="m"
                        noShadow
                        onClick={() => setButtonPalette("primary")}
                        palette={buttonPalette}
                        icon={NotificationsIcon}
                    />
                </Badge>
            </Box>
            <Popover
                open={open}
                onClose={closePopover}
                anchorEl={anchorEl}
                className={popoverStyles.root}
                classes={isMobileScreenSize ? fullScreenPopoverClasses : popoverClasses}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
            >
                <NotificationsContainer
                    close={closePopover}
                    notifications={notifications}
                    onDeleteNotification={deleteNotification}
                    onDeleteNotifications={deleteAllNotifications}
                    isLoading={isLoading}
                />
            </Popover>
        </>
    );
};

export default observer(NotificationsButton);
